import { Component, OnInit, Inject, ViewChild, ElementRef } from '@angular/core';
import { ReducerUtilityService, StateStructure } from '@ptl/state';
import { UntypedFormGroup, UntypedFormBuilder, Validators, ValidatorFn, ValidationErrors, AbstractControl } from '@angular/forms';
import { PtlWindowLocationService } from '@ptl/global-components';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'; 
import { DobCheckServiceResponse, FormControls, FormValuesAsJson } from './interfaces/dob-check-modal.interface';
import moment from 'moment';
import { DobCheckModalService } from './dob-check-modal.service';
import { HttpErrorResponse } from '@angular/common/http';
import { NgRedux } from '@ptl/angular-redux-store';
import { isDobAuthorized } from 'src/app/angular-elements/producer/saved-quotes/reducers/saved-quotes.reducer';

@Component({
  selector: 'app-dob-check-modal',
  templateUrl: './dob-check-modal.component.html',
  styleUrl: './dob-check-modal.component.scss',
  standalone: false
})
export class DobCheckModalComponent implements OnInit {

  isValidDob: boolean;
  attemptCount: number = 0;
  enableSendNewQuote: boolean = false;
  referralDob: string; 
  formattedReferralDob: string;
  entryMask: string = '00/00/0000'
  dobFailed: boolean = false;
  isChildOnly: boolean = false
  referralId: string;

  form: UntypedFormGroup;

  @ViewChild('entryRef') entryRef: ElementRef;
  dateRange = {
    min: '1900-01-01',
    minErrorFormat: '01/01/1900',
    max: '2200-01-01',
    maxErrorFormat: '01/01/2020',
  }
  

  constructor(
    private dialogRef: MatDialogRef<DobCheckModalComponent>,
    private fb: UntypedFormBuilder,
    private reducerUtilityService: ReducerUtilityService,
    private windowLocationService: PtlWindowLocationService,
    private dobCheckService: DobCheckModalService,
    private ngRedux: NgRedux<StateStructure>,
    @Inject(MAT_DIALOG_DATA) public data: {
      disableClose: boolean
      disabledLinkUrl: string,
      modalContent: {
        heading: string,
        subheading: string,
        childOnlySubheading: string,
        buttonLabel: string,
        errorMessage: string
      }
    }
  ) {}

  ngOnInit(){ 
    this.isChildOnly = this.reducerUtilityService.getPayloadStates().individualMarket.childOnlyPolicy === "No" ? false : true;
    this.referralDob = this.isChildOnly ? this.reducerUtilityService.getPayloadStates().individualMarket.ratingInfo.dependents[0].dob : this.reducerUtilityService.getPayloadStates().individualMarket.ratingInfo.subscriber[0].dob;
    this.formattedReferralDob = moment.utc(this.referralDob).format('MM/DD/YYYY');
    this.referralId = this.reducerUtilityService.getComponentStates().SavedQuotes.referralId;

    this.form = this.fb.group({
      dob: this.fb.control(null, [
        Validators.required,
        Validators.maxLength(11),
        this.dobConfirmedValidator()
      ]),
    })

    this.setValidators();
  }

  async validateDob(){
      const dobUTC = (this.form.get('dob').value).toISOString()
      const dobEncoded = window.btoa(dobUTC) //convert to UTC format and encode in base 64
      const dobAttemptResponse: DobCheckServiceResponse | HttpErrorResponse = await this.dobCheckService.validateQuoteAccess(dobEncoded, this.referralId);
      const successUrl = window.location.href;
      const disabledLinkUrl = this.data?.disabledLinkUrl ? this.data?.disabledLinkUrl : '/dob-error';
      const authorized = 'payload' in dobAttemptResponse ? dobAttemptResponse.payload.authorized : false;
      const attemptCount = 'payload' in dobAttemptResponse ? dobAttemptResponse.payload.attempts : 0;

      //call service, if dob matches go to url, else update attempt counts and try again until 3 counts then redirect

      if(authorized === true && this.form.get('dob').value){
        this.dialogRef.close();
        this.ngRedux.dispatch(isDobAuthorized(true));
        setTimeout(()=>{this.windowLocationService.goToUrl(successUrl)},500);
      }else{
        if(attemptCount >= 3){
          this.dialogRef.close();
          this.windowLocationService.goToUrl(disabledLinkUrl);
          return;
        }
        const wrongDobError = this.data.modalContent.errorMessage ? this.data.modalContent.errorMessage : 'Date of birth does not match our records, please try again. You have ' + (3-attemptCount) + ' attempts remaining';
        this.form.get('dob').setErrors({incorrectDob: wrongDobError });
      }
  }

  setValidators(){
    this.formControls.dob.setValidators([
      Validators.required,
      Validators.maxLength(11),
    ]);
    this.formControls.dob.updateValueAndValidity();
    this.form.updateValueAndValidity();
  }

  dobConfirmedValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value && control.value !== this.formattedReferralDob) {
        return { dobNoMatch: true};
      }
      return null;
    };
  }

  get formValuesAsJson(): FormValuesAsJson {
    // form.value does not return disabled control values; getRawValue() does
    return this.form.getRawValue() as unknown as FormValuesAsJson;
  }

  get formControls(): FormControls {
    return this.form.controls as unknown as FormControls;
  }

}
